import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import dayjs, { Dayjs } from "dayjs";
import { RootState } from "../store/reducers";
import transactionApi from "../store/modules/transactionYear/transactionApi";
import { TransactionStatus } from "../store/repsaApi";

export { TransactionStatus };

export const PIPELINE_ROUTE = "pipeline";

export const isPipelineStatus = (status?: TransactionStatus | null) =>
  status === TransactionStatus.Lead || status === TransactionStatus.Active;

export const TRANSACTION_STATUS_OPTIONS = [
  { value: TransactionStatus.Lead, label: "Lead" },
  { value: TransactionStatus.Active, label: "Active" },
  { value: TransactionStatus.Transaction, label: "Transaction" },
];

export const transactionStatusLabel = (status?: TransactionStatus) =>
  TRANSACTION_STATUS_OPTIONS.find((o) => o.value === status)?.label ??
  "Transaction";

/** "Closing Date" for Transactions, "Potential Closing Date" for Lead/Active */
export const closingDateLabel = (status?: TransactionStatus | null) =>
  isPipelineStatus(status) ? "Potential Closing Date" : "Closing Date";

/** Potential Closing Date already in the past */
export const isOverdueEntry = (
  status?: TransactionStatus | null,
  date?: Dayjs | string | null,
) => isPipelineStatus(status) && !!date && dayjs(date).isBefore(dayjs(), "day");

/** The Pipeline feature can be turned off in Settings > Category Options */
export const usePipelineEnabled = () => {
  const { hiddenFields } = useSelector(
    (state: RootState) => state.hiddenFields,
  );
  return !hiddenFields?.some((field) => field.name === "pipeline");
};

/** Sort key standing in for the Pipeline in chart period lists (sorts after every real year) */
export const PIPELINE_CHART_YEAR = 9999;

/** Display value for a chart period year ("Pipeline" for the Pipeline pseudo-year) */
export const chartYearLabel = (year?: number) =>
  year === PIPELINE_CHART_YEAR ? "Pipeline" : year;

/**
 * Year folders for analytics charts. When the Pipeline folder is part of the selection
 * it is appended as a pseudo-year so its rows get a period labelled "Pipeline".
 */
export const useChartYears = (selectedYearIds?: number[]) => {
  const { years } = useSelector((state: RootState) => state.transactionYear);
  const pipelineEnabled = usePipelineEnabled();
  const { data: pipelineFolder } = usePipelineFolder(pipelineEnabled);
  const pipelineId = pipelineFolder?.id;
  return useMemo(
    () =>
      pipelineId !== undefined && selectedYearIds?.includes(pipelineId)
        ? [...years, { id: pipelineId, year: PIPELINE_CHART_YEAR }]
        : years,
    [years, pipelineId, selectedYearIds],
  );
};

/** The user's Pipeline folder (its year id, entry and overdue counts) */
export const usePipelineFolder = (enabled: boolean = true) => {
  const { selectedCoAgentIds } = useSelector(
    (state: RootState) => state.coAgentFilter,
  );
  return useQuery({
    queryKey: ["pipelineFolder", selectedCoAgentIds],
    queryFn: () => transactionApi.getPipelineFolder(),
    enabled,
  });
};
